import type { AreaMapObject } from "@corvus/corvus-map";
import type { MeasurementPoseMode } from "@corvus/corvus-map/model";
import { useMemo } from "react";

import type {
    MeasurementImageCorners,
    MeasurementMosaicSource,
} from "./measurementMosaic";
import {
    getMeasurementThumbnailPath,
    projectMeasurementImage,
} from "./measurementMosaic";

interface UseProjectedMeasurementImagesOptions {
    enabled: boolean;
    organizationId?: string;
    locationId?: string;
    measurements?: readonly MeasurementMosaicSource[] | null;
    areas: readonly AreaMapObject[];
    poseMode: MeasurementPoseMode;
}

export interface ProjectedMeasurementImage {
    id: string;
    corners: MeasurementImageCorners;
    thumbnailPath: string | null;
}

export function useProjectedMeasurementImages({
    enabled,
    organizationId,
    locationId,
    measurements,
    areas,
    poseMode,
}: UseProjectedMeasurementImagesOptions): readonly ProjectedMeasurementImage[] {
    return useMemo(() => {
        if (
            !enabled ||
            !organizationId ||
            !locationId ||
            !Array.isArray(measurements)
        ) {
            return [];
        }

        return measurements.flatMap(
            (measurement): ProjectedMeasurementImage[] => {
                if (
                    typeof measurement.id !== "string" ||
                    measurement.id.length === 0
                ) {
                    return [];
                }

                const corners = projectMeasurementImage(
                    measurement,
                    areas,
                    poseMode,
                );
                if (!corners) return [];

                return [
                    {
                        id: measurement.id,
                        corners,
                        thumbnailPath:
                            measurement.deletedAt === undefined
                                ? getMeasurementThumbnailPath(
                                      organizationId,
                                      locationId,
                                      measurement.id,
                                  )
                                : null,
                    },
                ];
            },
        );
    }, [areas, enabled, locationId, measurements, organizationId, poseMode]);
}
